import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert, Button, Descriptions, Modal, Space, Upload } from 'antd';
import { MailOutlined, PaperClipOutlined } from '@ant-design/icons';
import { API } from '@/services/api.service';
import { date, errorText, money } from './format';

/**
 * The overdue notice of one return: the due date has passed and the platform
 * sees no payment, so the accountant sends the project's people a reminder to
 * pay, with the payment slip and, if they have it, the balance statement from
 * ePorezi. Sent by hand only: without the project's statements nobody but the
 * accountant can tell whether it was paid.
 */
export default function OverdueNoticeModal({ open, company, taxReturn: r, onClose, onSent }) {
  const { t } = useTranslation('taxReturns');
  const [file, setFile] = useState(null);
  const [showMail, setShowMail] = useState(false);

  const { data: notice, error, isLoading } = useQuery({
    queryKey: ['tax-overdue-notice', company, r?._id],
    queryFn: () => API.taxReturns.getOverdueNotice(company, r._id),
    enabled: Boolean(open && company && r?._id),
  });

  const send = useMutation({
    mutationFn: () => API.taxReturns.sendOverdueNotice(company, r._id, file),
    onSuccess: () => {
      setFile(null);
      onSent?.();
      onClose();
    },
  });

  const close = () => {
    setFile(null);
    setShowMail(false);
    send.reset();
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={close}
      title={t('overdueNotice.title')}
      width={720}
      destroyOnClose
      footer={
        <Space>
          <Button shape="round" onClick={close}>
            {t('overdueNotice.cancel')}
          </Button>
          <Button
            shape="round"
            type="primary"
            danger
            icon={<MailOutlined />}
            loading={send.isPending}
            disabled={!notice?.to?.length}
            onClick={() => send.mutate()}
          >
            {t('overdueNotice.send')}
          </Button>
        </Space>
      }
    >
      <p className="mb-3 text-sm opacity-70">{t('overdueNotice.help')}</p>
      {error && <Alert type="error" showIcon className="mb-3" message={errorText(t, error)} />}
      {notice && (
        <>
          {notice.sent > 0 && (
            <Alert
              type="info"
              showIcon
              className="mb-3"
              message={t('overdueNotice.alreadySent', { count: notice.sent, date: date(notice.lastSentAt) })}
            />
          )}
          {!notice.to.length && <Alert type="warning" showIcon className="mb-3" message={t('errors.notify_overdue_no_recipients')} />}
          <Descriptions size="small" column={1} bordered className="mb-4">
            <Descriptions.Item label={t('overdueNotice.open')}>{money(notice.open)}</Descriptions.Item>
            <Descriptions.Item label={t('overdueNotice.to')}>{notice.to.join(', ') || '—'}</Descriptions.Item>
            <Descriptions.Item label={t('overdueNotice.cc')}>{notice.cc.join(', ') || '—'}</Descriptions.Item>
            <Descriptions.Item label={t('overdueNotice.subject')}>{notice.subject}</Descriptions.Item>
          </Descriptions>
          <Upload
            accept=".pdf,application/pdf"
            maxCount={1}
            fileList={file ? [{ uid: '1', name: file.name, status: 'done' }] : []}
            beforeUpload={(f) => {
              setFile(f);
              return false;
            }}
            onRemove={() => setFile(null)}
          >
            <Button shape="round" icon={<PaperClipOutlined />}>
              {t('overdueNotice.attach')}
            </Button>
          </Upload>
          <p className="mt-1 mb-3 text-xs opacity-60">{t('overdueNotice.attachHelp')}</p>
          <Button shape="round" size="small" onClick={() => setShowMail((v) => !v)}>
            {showMail ? t('overdueNotice.hideMail') : t('overdueNotice.showMail')}
          </Button>
          {showMail && (
            <iframe
              title="mail"
              srcDoc={notice.html}
              sandbox=""
              className="mt-3"
              style={{ width: '100%', height: '60vh', border: '1px solid #e5e7eb', borderRadius: 8 }}
            />
          )}
        </>
      )}
      {isLoading && <p className="text-sm opacity-60">…</p>}
      {send.isError && <Alert type="error" showIcon className="mt-3" message={errorText(t, send.error)} />}
    </Modal>
  );
}
